import { useAppSelector } from '../../../../core/hooks/storeHooks';
import { UseFilterDropdown } from '../../../hooks/useFilterDropdown';
import { UseSheetTheme } from '../../../hooks/useSheetTheme';
import {
  FilterValueMode,
  FilterCondition,
  FilterValues,
  FilterSettings,
  Filter,
} from '../../../types';
import { useEffect, useState } from 'react';
import {
  SheetAndView,
  selectSheetAndView,
} from '../../../selectors/sheetSelector';
import {
  getUniqueCellValuesUnderColumn,
  getUniqueColorsUnderColumnForFilter,
} from '../../../helpers/filterHelpers/filterHelpers';
import {
  getColumnFilter,
  getCurrentFilters,
  isFilterValueChanged,
} from '../../../utils/filter/filter';
import { validateFilterConfigs } from '../../../helpers/filterHelpers/validateFilterConfigs';

// #region types
export interface FilterDropdownProps
  extends UseFilterDropdown,
    Pick<UseSheetTheme, 'fill' | 'textColor' | 'stroke' | 'isDarkTheme'> {
  sheetId: string;
}

export enum FilterTabs {
  Value = 'value',
  Condition = 'condition',
  TextColor = 'text color',
  FillColor = 'fill color',
  Settings = 'settings',
}

interface VisibleCellValues {
  key: string; // lowercase value of the original
  visibleValue: string;
}
export interface FilterConfigsState {
  tab: FilterTabs;
  cellValues: VisibleCellValues[];
  filterValues: FilterValues;
  valueMode: FilterValueMode;
  condition?: FilterCondition;
  firstText: string;
  secondText: string;
  textColor: string;
  fillColor: string;
  textColors: string[];
  fillColors: string[];
  filterSettings: FilterSettings;
  errors: {
    [key: string]: string;
  };
}

export interface UseFilterConfigs extends FilterConfigsState {
  switchTab: (tab: FilterTabs) => void;
  changeCondition: (condition?: FilterCondition) => void;
  changeValue: (key: string, value: string) => void;
  toggleSelectAll: (value: boolean) => void;
  selectValue: (
    visibleValue: string,
    originalValue: string,
    isSelected: boolean
  ) => void;
  changeFilterSettings: (key: string, value: string | boolean) => void;
  clearFilter: () => void;
  applyFilter: () => void;
}

// #endregion

export default (props: FilterDropdownProps): UseFilterConfigs => {
  const colIndex = props.filterDropdownState.colIndex;
  const { sheet, sheetView }: SheetAndView = useAppSelector((state) =>
    selectSheetAndView(state, props.sheetId)
  );

  const [state, setState] = useState<FilterConfigsState>({
    tab: FilterTabs.Value,
    cellValues: [],
    filterValues: {},
    valueMode: FilterValueMode.Deselect,
    firstText: '',
    secondText: '',
    textColor: '',
    fillColor: '',
    textColors: [],
    fillColors: [],
    filterSettings: {},
    errors: {},
  });

  // useEffect(() => {
  //   const filters = getCurrentFilters(sheet, sheetView);
  //   const filter = getColumnFilter(colIndex, filters, sheet.cols);

  //   const { textColors, fillColors } = getUniqueColorsUnderColumnForFilter(
  //     colIndex,
  //     sheet,
  //     filter
  //   );
  //   const cellValues = getUniqueCellValuesUnderColumn(
  //     colIndex,
  //     sheet,
  //     sheetView.filterResults,
  //     filter
  //   );

  //   setState({
  //     ...state,
  //     cellValues,
  //     valueMode: filter?.value?.valueMode ?? FilterValueMode.Deselect,
  //     filterValues: filter?.value?.values ?? {},
  //     firstText: filter?.condition?.firstText ?? '',
  //     secondText: filter?.condition?.secondText ?? '',
  //     textColors,
  //     fillColors,
  //     textColor: filter?.textColor ?? '',
  //     fillColor: filter?.fillColor ?? '',
  //     filterSettings: { ...sheet.filterSettings },
  //   });
  // }, []);

  const switchTab = (tab: FilterTabs) => {
    return setState({ ...state, tab });
  };

  const changeCondition = (condition?: FilterCondition) => {
    setState({
      ...state,
      condition,
      firstText: '',
      secondText: '',
    });
  };

  const changeValue = (key: string, value: string) => {
    setState({
      ...state,
      fillColor: '',
      textColor: '',
      [key]: value,
    });
  };

  const toggleSelectAll = (value: boolean) => {
    setState({
      ...state,
      valueMode: value ? FilterValueMode.Deselect : FilterValueMode.Select,
      filterValues: {},
    });
  };

  const selectValue = (
    key: string,
    visibleValue: string,
    isSelected: boolean
  ) => {
    let filterValues = { ...state.filterValues };
    let valueMode = state.valueMode;
    const cellValuesLength = state.cellValues.length;

    // Switch Modes based on selectedValues i.e filterValues
    if (state.valueMode === FilterValueMode.Deselect) {
      if (isSelected) delete filterValues[key];
      else filterValues[key] = visibleValue;

      const filterValuesLength = Object.keys(filterValues).length;
      if (filterValuesLength === cellValuesLength) {
        filterValues = {};
        valueMode = FilterValueMode.Select;
      }
    } else {
      if (isSelected) filterValues[key] = visibleValue;
      else delete filterValues[key];

      const filterValuesLength = Object.keys(filterValues).length;
      if (filterValuesLength === cellValuesLength) {
        filterValues = {};
        valueMode = FilterValueMode.Deselect;
      }
    }

    setState({
      ...state,
      filterValues,
      valueMode,
    });
  };

  const changeFilterSettings = (key: string, value: string | boolean) => {
    setState({
      ...state,
      filterSettings: {
        ...state.filterSettings,
        [key]: value,
      },
    });
  };

  const convertStateToFilter = () => {
    // const isValueChanged =
    //   state.valueMode === FilterValueMode.Select ||
    //   !!Object.keys(state.filterValues).length;

    const filter: Filter = {};
    if (isFilterValueChanged(state.valueMode, state.filterValues)) {
      filter.value = {
        values: state.filterValues,
        valueMode: state.valueMode,
      };
    }
    if (state.condition) {
      filter.condition = {
        type: state.condition,
        firstText: state.firstText,
        secondText: state.secondText,
      };
    }
    if (state.textColor) filter.textColor = state.textColor;
    else if (state.fillColor) filter.fillColor = state.fillColor;

    return filter;
  };

  const clearFilter = () => {};

  const applyFilter = () => {
    const { errors, isValid } = validateFilterConfigs(
      state.firstText,
      state.secondText,
      state.condition
    );
    console.log('errors', errors, isValid);
    setState({ ...state, errors });
    if (!isValid) {
      return;
    }
    const filter = convertStateToFilter();
    const filterSettings = state.filterSettings;
  };

  return {
    switchTab,
    ...state,
    changeCondition,
    changeValue,

    toggleSelectAll,
    selectValue,
    changeFilterSettings,
    clearFilter,
    applyFilter,
  };
};
